/**
 * Persistent groups of vector objects.
 *
 * Growing a selection infers a figure from geometry every time you ask. That is
 * the right default for artwork nobody has organised, and it is a guess: nothing
 * geometric distinguishes "a shirt touching a person" from "two people
 * touching". Once you have accepted a figure, the answer should be recorded
 * rather than re-derived, so a later click selects exactly what you grouped.
 *
 * Membership lives on the objects, as a tag in their extension bag, following
 * the pattern `live-paint.ts` established — read defensively, never trusted.
 * Names live in a registry on the scene. Objects never appear in the registry
 * and the registry never holds object ids, so neither half can contradict the
 * other: an object tagged with a group that has no registry entry is simply
 * ungrouped-with-a-name-missing, not a dangling reference.
 *
 * Flat and exclusive, deliberately. Nested groups would force a schema version
 * bump and new persistence validation to buy a capability that growing a
 * selection already covers.
 */

import { changed, rejected, type Command, type CommandExecution } from '../core/commands';
import { damage } from '../core/damage';
import { createId, findLayer, type EditorDocument, type LayerId, type VectorLayer } from '../core/model';
import type { VectorObject, VectorObjectId } from './types';

export const OBJECT_GROUP_EXTENSION = 'maipo:object-group';
export const OBJECT_GROUPS_EXTENSION = 'maipo:object-groups';

export interface ObjectGroup { readonly id: string; readonly name: string }

interface GroupTag { groupId: string }

function groupTag(object: VectorObject): GroupTag | null {
  const value = object.extensions[OBJECT_GROUP_EXTENSION];
  if (!value || typeof value !== 'object') return null;
  const tag = value as Partial<GroupTag>;
  return typeof tag.groupId === 'string' && tag.groupId.length > 0 ? { groupId: tag.groupId } : null;
}

export function groupIdOf(object: VectorObject | undefined): string | null {
  return object ? groupTag(object)?.groupId ?? null : null;
}

/** Named groups on the scene. Order is meaningful, so it is an array. */
export function objectGroups(layer: VectorLayer): ObjectGroup[] {
  const value = layer.vector.extensions[OBJECT_GROUPS_EXTENSION];
  if (!value || typeof value !== 'object') return [];
  const record = value as { version?: unknown; groups?: unknown };
  // A version this reader does not know is not partially trusted.
  if (record.version !== 1 || !Array.isArray(record.groups)) return [];
  return record.groups.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const group = entry as Partial<ObjectGroup>;
    return typeof group.id === 'string' && typeof group.name === 'string' ? [{ id: group.id, name: group.name }] : [];
  });
}

/** Every object of the group `objectId` belongs to, in scene order. */
export function groupMembers(layer: VectorLayer, objectId: VectorObjectId): VectorObjectId[] {
  const groupId = groupIdOf(layer.vector.objects.find((object) => object.id === objectId));
  if (!groupId) return [objectId];
  return layer.vector.objects.filter((object) => groupIdOf(object) === groupId).map((object) => object.id);
}

/**
 * Expands a selection to whole groups.
 *
 * Used by clicking, and deliberately NOT by growing or by selecting the same
 * colour: colour identity and geometry grouping are the two independent axes
 * this editor is built around, and expanding groups inside a colour operation
 * would collapse one into the other.
 */
export function expandToGroups(layer: VectorLayer, objectIds: readonly VectorObjectId[]): VectorObjectId[] {
  const groups = new Set<string>();
  const direct = new Set(objectIds);
  for (const object of layer.vector.objects) {
    if (!direct.has(object.id)) continue;
    const groupId = groupIdOf(object);
    if (groupId) groups.add(groupId);
  }
  if (groups.size === 0) return [...objectIds];
  return layer.vector.objects
    .filter((object) => direct.has(object.id) || groups.has(groupIdOf(object) ?? ''))
    .map((object) => object.id);
}

/**
 * Tags a set of objects with a group, or clears their tags when `groupId` is
 * null.
 *
 * Narrow on purpose. `UpdateVectorSceneCommand` would do this too, but it
 * structuredClones the whole scene three times and stringifies it twice per
 * operation, and replaces every object so the geometry-keyed `Path2D` cache
 * misses for the entire layer. Grouping changes no geometry, so this stores only
 * the previous tag per object and mutates the extension bag in place.
 */
export class TagObjectGroupCommand implements Command {
  readonly label: string;
  private before?: Map<VectorObjectId, unknown>;
  private beforeGroups?: unknown;
  private hadGroups = false;
  private readonly groupId: string;

  constructor(
    private readonly layerId: LayerId,
    private readonly objectIds: readonly VectorObjectId[],
    private readonly grouping: boolean,
    private readonly name?: string,
  ) {
    this.label = grouping ? 'Group objects' : 'Ungroup objects';
    this.groupId = createId('group');
  }

  retainedBytes(): number { return 192 + this.objectIds.length * 48 + (this.before?.size ?? 0) * 64; }

  private layer(document: EditorDocument): VectorLayer | undefined {
    const layer = findLayer(document.root, this.layerId);
    return layer?.kind === 'vector' ? layer : undefined;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = this.layer(document);
    if (!layer) return rejected('Vector layer no longer exists.');
    if (layer.locked) return rejected('Layer is locked.');
    // Ungrouping works from whole groups, so selecting one member releases all
    // of them rather than stranding the rest under a name nothing references.
    const targets = this.grouping
      ? layer.vector.objects.filter((object) => this.objectIds.includes(object.id))
      : layer.vector.objects.filter((object) => {
        const groupId = groupIdOf(object);
        return groupId !== null && this.objectIds.some((id) => groupIdOf(layer.vector.objects.find((entry) => entry.id === id)) === groupId);
      });
    if (targets.length === 0) return rejected(this.grouping ? 'Select at least two objects to group.' : 'Nothing selected is grouped.');
    if (this.grouping && targets.length < 2) return rejected('Select at least two objects to group.');

    this.before ??= new Map(targets.map((object) => [object.id, structuredClone(object.extensions[OBJECT_GROUP_EXTENSION])]));
    this.hadGroups = Object.hasOwn(layer.vector.extensions, OBJECT_GROUPS_EXTENSION);
    this.beforeGroups ??= structuredClone(layer.vector.extensions[OBJECT_GROUPS_EXTENSION] ?? null);

    const existing = objectGroups(layer);
    if (this.grouping) {
      for (const object of targets) object.extensions[OBJECT_GROUP_EXTENSION] = { groupId: this.groupId };
      const name = this.name?.trim() || `Group ${existing.length + 1}`;
      layer.vector.extensions[OBJECT_GROUPS_EXTENSION] = { version: 1, groups: [...existing, { id: this.groupId, name }] };
    } else {
      const released = new Set(targets.map((object) => groupIdOf(object)).filter((id): id is string => Boolean(id)));
      for (const object of targets) delete object.extensions[OBJECT_GROUP_EXTENSION];
      const remaining = existing.filter((group) => !released.has(group.id));
      if (remaining.length > 0) layer.vector.extensions[OBJECT_GROUPS_EXTENSION] = { version: 1, groups: remaining };
      else delete layer.vector.extensions[OBJECT_GROUPS_EXTENSION];
    }
    // Structural, not visual: nothing about the artwork's pixels changed, but
    // the layer panel and selection UI have to notice.
    return changed(damage({ layerIds: [this.layerId] }));
  }

  undo(document: EditorDocument): void {
    const layer = this.layer(document);
    if (!layer || !this.before) return;
    for (const object of layer.vector.objects) {
      if (!this.before.has(object.id)) continue;
      const previous = this.before.get(object.id);
      if (previous === undefined) delete object.extensions[OBJECT_GROUP_EXTENSION];
      else object.extensions[OBJECT_GROUP_EXTENSION] = structuredClone(previous);
    }
    if (this.hadGroups) layer.vector.extensions[OBJECT_GROUPS_EXTENSION] = structuredClone(this.beforeGroups);
    else delete layer.vector.extensions[OBJECT_GROUPS_EXTENSION];
  }
}

/** True when anything selected already belongs to a group. */
export function hasGroupedObject(layer: VectorLayer, objectIds: readonly VectorObjectId[]): boolean {
  return layer.vector.objects.some((object) => objectIds.includes(object.id) && groupIdOf(object) !== null);
}
